<script setup lang="ts">
/**
 * GALERIE de la fiche produit — disposition Zalando.
 *
 * DESKTOP (lg+) : colonne VERTICALE de vignettes À GAUCHE + grande image à
 * droite. C'est la disposition des e-commerces mode (Zalando, Asos, Zara) :
 * les vignettes ne mangent pas de hauteur sous l'image, donc la photo
 * principale reste entièrement au-dessus de la ligne de flottaison, et le
 * nombre de photos est lisible d'un coup d'œil sans scroll horizontal.
 *
 * MOBILE : bande HORIZONTALE sous l'image. En dessous de ~640 px de large, une
 * colonne de vignettes amputerait la photo principale de 25 % de sa largeur
 * pour un gain nul — Baymard documente que l'image produit est l'élément le
 * plus regardé d'une fiche.
 *
 * Le composant ne fait QUE de l'affichage : l'index courant est piloté par le
 * parent via `v-model` (la page le remet à 0 quand le produit change).
 */
const props = defineProps<{
  /** File IDs Directus des photos du produit (peut être vide). */
  imageIds: string[];
  /** Nom du produit — sert d'`alt` sur la photo principale. */
  productName: string;
  /** Base Directus pour la construction des URLs d'assets. */
  directusUrl: string;
  /** URLs pleine résolution passées à la lightbox. */
  fullImages: string[];
}>();

/** Index de la photo affichée en grand — piloté par le parent. */
const currentIndex = defineModel<number>({ required: true });

const lightboxRef = ref<{ open: (index: number) => void } | null>(null);

function imageUrl(id: string, size: number): string {
  return `${props.directusUrl}/api/assets/${id}?width=${size}&height=${size}&fit=cover&format=webp&quality=85`;
}

const hasImages = computed(() => props.imageIds.length > 0);
const hasThumbnails = computed(() => props.imageIds.length > 1);

/** Photo affichée en grand — repli sur la 1re si l'index est hors bornes. */
const mainImageId = computed(
  () => props.imageIds[currentIndex.value] || props.imageIds[0] || "",
);

function openLightbox() {
  lightboxRef.value?.open(currentIndex.value);
}
</script>

<template>
  <!--
    Pas de carte autour de la galerie (Karim 2026-08-09) : les visuels
    flottent directement sur le fond de page. Une carte blanche autour d'une
    image encadre une image — double bordure visuelle sans information.
  -->
  <div>
    <!--
      `wide:flex-row` = vignettes à GAUCHE de l'image (Zalando).
      `flex-col-reverse` en dessous : l'image principale reste EN PREMIER dans
      le flux visuel mobile, la bande de vignettes passe dessous — sans changer
      l'ordre du DOM, donc l'ordre de tabulation reste image → vignettes.
    -->
    <div class="flex flex-col-reverse wide:flex-row gap-2 wide:gap-3">
      <!-- Vignettes : colonne verticale en lg, bande horizontale scrollable en dessous -->
      <div
        v-if="hasThumbnails"
        class="flex wide:flex-col gap-2 overflow-x-auto wide:overflow-x-visible wide:overflow-y-auto pb-1 wide:pb-0 wide:max-h-[520px] wide:w-[76px] wide:shrink-0 boutique-thumbs"
      >
        <button
          v-for="(id, idx) in imageIds"
          :key="id"
          type="button"
          class="w-16 h-16 wide:w-[68px] wide:h-[68px] rounded-xl overflow-hidden shrink-0 border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-500"
          :class="idx === currentIndex
            ? 'border-[#212121] dark:border-white'
            : 'border-gray-200 dark:border-[#333] hover:border-gray-400'"
          :aria-label="$t('boutique.productGallery.ariaPhoto', { numero: idx + 1, total: imageIds.length })"
          :aria-current="idx === currentIndex"
          @click="currentIndex = idx"
        >
          <img :src="imageUrl(id, 200)" alt="" loading="lazy" class="w-full h-full object-cover" />
        </button>
      </div>

      <!-- Image principale -->
      <div class="flex-1 min-w-0">
        <button
          v-if="hasImages"
          type="button"
          class="block w-full aspect-square rounded-3xl overflow-hidden bg-gray-100 dark:bg-[#1a1a1a] cursor-zoom-in"
          :aria-label="$t('boutique.productGallery.ariaAgrandir')"
          @click="openLightbox"
        >
          <img
            :src="imageUrl(mainImageId, 1000)"
            :alt="productName"
            class="w-full h-full object-cover"
          />
        </button>
        <div
          v-else
          class="w-full aspect-square rounded-3xl bg-gray-100 dark:bg-[#1a1a1a] flex items-center justify-center"
        >
          <UIcon name="i-lucide-image" class="size-10 text-gray-300 dark:text-gray-600" />
        </div>
      </div>
    </div>

    <ImageLightbox v-if="hasImages" ref="lightboxRef" :images="fullImages" />
  </div>
</template>

<style scoped>
/* Barre de défilement discrète sur la colonne de vignettes : une scrollbar
   système large mangerait la moitié d'une vignette sur Windows. */
.boutique-thumbs {
  scrollbar-width: thin;
}
.boutique-thumbs::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
/* Gris PLEIN (règle projet `ui_no_bg_transparency`) — un noir à 15 %
   d'opacité prenait la teinte de ce qui passait dessous. */
.boutique-thumbs::-webkit-scrollbar-thumb {
  background: #d4d4d8;
  border-radius: 9999px;
}
.dark .boutique-thumbs::-webkit-scrollbar-thumb {
  background: #3f3f46;
}
</style>
